import { Alert, Button, Flex, Link, Text } from '@backstage/ui';
import { useState } from 'react';
import { incidentUrl } from '@internal/backstage-plugin-neo4j-notices-common';
import clsx from 'clsx';
import { useDismissNotice } from '../../hooks/useNoticeMutations';
import { useNotices } from '../../hooks/useNotices';
import type { Notice } from '../../types';
import { QueryClientProvider } from '../QueryClientProvider';
import classes from './NoticesPopUp.module.css';

/** Keep in step with the transition on `.lip`. */
const DISMISS_ANIMATION_MS = 160;

type NoticeAlertProps = {
  notice: Notice;
  isPending: boolean;
  onDismiss: (id: string) => void;
};

/**
 * Notices banner for a tree that already has a `QueryClientProvider`. Use
 * `NoticesPopUp` when the tree does not have one.
 */
export const NoticesPopUpContent = () => {
  const { data } = useNotices({ poll: true });
  const dismiss = useDismissNotice();
  const [leaving, setLeaving] = useState<string[]>([]);
  const [dismissing, setDismissing] = useState<string[]>([]);
  const [hidden, setHidden] = useState<string[]>([]);

  const visible = (data ?? []).filter(
    notice =>
      (!notice.dismissed ||
        dismissing.includes(notice.id) ||
        leaving.includes(notice.id)) &&
      !hidden.includes(notice.id),
  );

  // Collapse the lip only after the server confirms the dismiss, so a failed
  // call keeps the notice and the mutation hook posts the toast. `mutateAsync`
  // rejects for every call, while the `onError` of `mutate` runs only for the
  // latest call.
  const handleDismiss = (id: string) => {
    if (dismissing.includes(id)) {
      return;
    }
    setDismissing(prev => [...prev, id]);
    void dismiss.mutateAsync(id).then(
      () => {
        setLeaving(prev => [...prev, id]);
        window.setTimeout(() => {
          setHidden(prev => [...prev, id]);
          setLeaving(prev => prev.filter(item => item !== id));
          setDismissing(prev => prev.filter(item => item !== id));
        }, DISMISS_ANIMATION_MS);
      },
      () => setDismissing(prev => prev.filter(item => item !== id)),
    );
  };

  // The banner shows on every page. While the list loads, or if it fails, it
  // shows nothing. A skeleton or an error does not belong on an unrelated page.
  if (visible.length === 0) {
    return null;
  }

  return (
    <Flex
      direction="column"
      gap="0"
      className={classes.host}
      role="region"
      aria-label="Notices"
    >
      {visible.map(notice => (
        <div
          key={notice.id}
          className={clsx(
            classes.lip,
            leaving.includes(notice.id) && classes.lipLeaving,
          )}
        >
          <div className={classes.lipInner}>
            <NoticeAlert
              notice={notice}
              isPending={dismissing.includes(notice.id)}
              onDismiss={handleDismiss}
            />
          </div>
        </div>
      ))}
    </Flex>
  );
};

/**
 * Notices banner that brings its own `QueryClientProvider`. Use it outside a
 * provider; inside one, use `NoticesPopUpContent`.
 */
export const NoticesPopUp = () => (
  <QueryClientProvider>
    <NoticesPopUpContent />
  </QueryClientProvider>
);

function NoticeAlert({ notice, isPending, onDismiss }: NoticeAlertProps) {
  return (
    <Alert
      status="warning"
      icon
      className={classes.alert}
      title={<span className={classes.title}>{notice.title}</span>}
      description={
        <Text as="span" variant="body-small" className={classes.text}>
          {notice.description}
        </Text>
      }
      customActions={
        <Flex align="center" gap="3">
          {notice.incidentId && (
            <Link
              href={incidentUrl(notice.incidentId)}
              target="_blank"
              rel="noreferrer"
              className={classes.link}
            >
              Monitor {notice.incidentId}
            </Link>
          )}
          <Button
            size="small"
            variant="tertiary"
            isPending={isPending}
            onPress={() => onDismiss(notice.id)}
          >
            Dismiss
          </Button>
        </Flex>
      }
    />
  );
}
